// TMOS Command Palette Component
// Date: December 9, 2025
// Purpose: Universal search and navigation interface (Cmd+K / Ctrl+K)
//
// Features:
// - Universal search across all entities
// - Quick actions (create, update, delete)
// - Recent items
// - Navigation shortcuts
// - RBAC-filtered results
// - Keyboard navigation

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { unifiedRBACService } from '@/services/unifiedRBACService';
import { iconService } from '@/services/iconService';
import {
  MagnifyingGlassIcon,
  ArrowRightIcon,
  PlusIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface CommandPaletteItem {
  id: string;
  type: 'navigation' | 'action' | 'search' | 'recent';
  category: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  shortcut?: string;
  path?: string;
  action?: () => void;
  keywords?: string[];
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * CommandPalette Component
 * 
 * Universal navigation and action interface triggered by Cmd+K (Mac) or Ctrl+K (Windows/Linux).
 * Provides instant access to all TMOS functionality through search.
 * 
 * Features:
 * - Fuzzy search across all navigation items and actions
 * - Recent items for quick access
 * - Quick actions for common tasks
 * - Keyboard navigation (arrows, enter, escape)
 * - RBAC-filtered results (only shows what user can access)
 * 
 * Business Rules:
 * - Only shows navigation items user has READ permission for
 * - Only shows actions user has CREATE/UPDATE/DELETE permission for
 * - Recent items tracked per user
 * - Search is case-insensitive and fuzzy
 */
export const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const translateNavLabel = useCallback((displayName: string, translationKey?: string) => {
    if (translationKey?.trim()) {
      const tr = t(translationKey);
      if (tr !== translationKey) return tr;
    }
    return displayName;
  }, [t]);

  // Helper function to render icon (supports emoji and heroicons)
  const renderIcon = (iconName: string | undefined) => {
    if (!iconName) return <span className="text-lg">📄</span>;
    
    // Check if iconName is an emoji (contains emoji characters)
    const isEmoji = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u.test(iconName);
    
    if (isEmoji) {
      return <span className="text-lg">{iconName}</span>;
    }
    
    // Use iconService for heroicons and other icon types
    const IconComponent = iconService.getIcon(iconName);
    return <IconComponent className="w-4 h-4" />;
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [items, setItems] = useState<CommandPaletteItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<CommandPaletteItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /**
   * Load all available navigation items and actions from database
   * Uses the same navigation service as Sidebar for consistency
   */
  const loadItems = useCallback(async () => {
    const navigationItems: CommandPaletteItem[] = [];
    const actionItems: CommandPaletteItem[] = [];
    const catNavigate = t('navigation.commandPalette.navigate');
    const catAdmin = t('navigation.commandPalette.administration');
    const catQuick = t('navigation.commandPalette.quickActions');

    try {
      // Load navigation items from database (same as Sidebar)
      const [businessTree, adminTree] = await Promise.all([
        unifiedRBACService.getCurrentUserNavigation('BUSINESS'),
        unifiedRBACService.getCurrentUserNavigation('ADMIN')
      ]);

      // Process all modules and screens
      // Track which modules came from admin tree to determine category
      const adminModuleIds = new Set(adminTree.modules.map(m => m.id));
      const allModules = [...businessTree.modules, ...adminTree.modules];
      
      for (const module of allModules) {
        // Determine category based on which tree the module came from
        const isAdminModule = adminModuleIds.has(module.id);
        const moduleCategory = isAdminModule ? catAdmin : catNavigate;
        
        // Add module itself if it has a route
        if (module.module_code) {
          const modulePath = `/modules/${module.module_code.toLowerCase().replace(/_/g, '-')}`;
          const moduleLabel = translateNavLabel(
            module.display_name || module.module_code,
            module.translation_key
          );
          
          navigationItems.push({
            id: `module-${module.id}`,
            type: 'navigation',
            category: moduleCategory,
            label: moduleLabel,
            description:
              module.description ||
              t('navigation.commandPalette.goToEntity', { name: moduleLabel }),
            path: modulePath,
            keywords: [
              module.display_name?.toLowerCase() || '',
              module.module_code.toLowerCase(),
              module.description?.toLowerCase() || ''
            ].filter(Boolean),
          });
        }

        // Add all screens within modules
        for (const screen of module.children || []) {
          // Drill-down templates such as /performance/kpis/:id are not real destinations.
          if (screen && screen.display_name && screen.has_read_permission && screen.route_path && !screen.route_path.includes(':')) {
            const screenCategory = isAdminModule ? catAdmin : catNavigate;
            const screenLabel = translateNavLabel(screen.display_name, screen.translation_key);
            
            navigationItems.push({
              id: `screen-${screen.id}`,
              type: 'navigation',
              category: screenCategory,
              label: screenLabel,
              description:
                screen.description ||
                t('navigation.commandPalette.goToEntity', { name: screenLabel }),
              path: screen.route_path,
              icon: renderIcon(screen.icon),
              keywords: [
                screen.display_name.toLowerCase(),
                screen.route_path.toLowerCase(),
                screen.description?.toLowerCase() || '',
                module.display_name?.toLowerCase() || ''
              ].filter(Boolean),
            });

            // Add "New" action for screens that support creation
            // Check if screen route suggests it's a list page (common patterns)
            if (screen.route_path && !screen.route_path.includes('/create') && !screen.route_path.includes('/edit')) {
              const createPath = `${screen.route_path}/create`;
              actionItems.push({
                id: `action-new-${screen.id}`,
                type: 'action',
                category: catQuick,
                label: t('navigation.commandPalette.newEntity', { name: screenLabel }),
                description: t('navigation.commandPalette.createEntity', { name: screenLabel }),
                path: createPath,
                keywords: [
                  `new ${screen.display_name.toLowerCase()}`,
                  `create ${screen.display_name.toLowerCase()}`,
                  `add ${screen.display_name.toLowerCase()}`,
                  screen.display_name.toLowerCase()
                ],
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to load navigation items for Command Palette:', error);
    }

    // Add some common quick actions that might not be in navigation
    // Use database icons: Risk (🛡️), Issue (🚨), Transformation (🚀), KPI (📈)
    const commonActions: CommandPaletteItem[] = [
      {
        id: 'action-new-risk',
        type: 'action',
        category: catQuick,
        label: t('navigation.quickActions.newRisk'),
        description: t('navigation.commandPalette.createEntity', {
          name: t('navigation.menuItems.risks'),
        }),
        path: '/risk?tab=overview&create=true',
        icon: renderIcon('🛡️'),
        keywords: ['new risk', 'create risk', 'add risk'],
      },
      {
        id: 'action-new-issue',
        type: 'action',
        category: catQuick,
        label: t('navigation.quickActions.newIssue'),
        description: t('navigation.commandPalette.createEntity', {
          name: t('navigation.menuItems.issues'),
        }),
        path: '/issues/create',
        icon: renderIcon('🚨'),
        keywords: ['new issue', 'create issue', 'add issue'],
      },
      {
        id: 'action-new-transformation',
        type: 'action',
        category: catQuick,
        label: t('navigation.quickActions.newTransformation'),
        description: t('navigation.commandPalette.createEntity', {
          name: t('navigation.menuItems.transformations'),
        }),
        path: '/transformations/create',
        icon: renderIcon('🚀'),
        keywords: ['new transformation', 'create transformation'],
      },
      {
        id: 'action-new-kpi',
        type: 'action',
        category: catQuick,
        label: t('navigation.commandPalette.newEntity', { name: t('navigation.menuItems.kpis') }),
        description: t('navigation.commandPalette.createEntity', {
          name: t('navigation.menuItems.kpis'),
        }),
        path: '/kpis/create',
        icon: renderIcon('📈'),
        keywords: ['new kpi', 'create kpi', 'add kpi'],
      },
    ];

    // Merge all items
    setItems([...navigationItems, ...actionItems, ...commonActions]);
  }, [t, translateNavLabel]);

  // Load navigation items and actions
  useEffect(() => {
    if (isOpen) {
      loadItems();
      // Focus input when opened
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      // Reset when closed
      setSearchQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen, loadItems]);

  // Filter items based on search query (same algorithm as Sidebar)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredItems(items);
      setSelectedIndex(0);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const queryWords = query.split(/\s+/).filter(Boolean);
    
    const filtered = items.filter(item => {
      // Build comprehensive searchable text (same as Sidebar)
      const searchableText = [
        item.label.toLowerCase(),
        item.description?.toLowerCase() || '',
        item.path?.toLowerCase() || '',
        ...(item.keywords || []).map(k => k.toLowerCase()),
      ].join(' ');

      // Support multi-word search (all words must match)
      const allWordsMatch = queryWords.every(word => searchableText.includes(word));
      
      // Also support exact phrase matching
      const phraseMatch = searchableText.includes(query);
      
      return allWordsMatch || phraseMatch;
    });

    // Group by category
    const grouped = filtered.reduce((acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    }, {} as Record<string, CommandPaletteItem[]>);

    // Flatten grouped items
    const flattened: CommandPaletteItem[] = [];
    Object.keys(grouped).forEach(category => {
      flattened.push(...grouped[category]);
    });

    setFilteredItems(flattened);
    setSelectedIndex(0);
  }, [searchQuery, items]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredItems.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredItems[selectedIndex]) {
          handleSelectItem(filteredItems[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredItems, selectedIndex, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  /**
   * Handle item selection
   */
  const handleSelectItem = (item: CommandPaletteItem) => {
    if (item.path) {
      navigate(item.path);
    } else if (item.action) {
      item.action();
    }
    onClose();
  };

  if (!isOpen) return null;

  // Group filtered items by category
  const groupedItems = filteredItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, CommandPaletteItem[]>);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Command Palette */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
          {/* Search Input */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('navigation.commandPalette.placeholder') || 'Search or type a command...'}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1 text-xs text-gray-400">
                <kbd className="px-2 py-1 bg-gray-100 rounded">⌘</kbd>
                <span>K</span>
              </div>
            </div>
          </div>

          {/* Results */}
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto p-2"
          >
            {filteredItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>{t('navigation.commandPalette.noResults') || 'No results found'}</p>
                <p className="text-xs mt-2">
                  {t('navigation.commandPalette.tryDifferent') || 'Try a different search term'}
                </p>
              </div>
            ) : (
              Object.keys(groupedItems).map(category => (
                <div key={category} className="mb-4">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {category}
                  </div>
                  {groupedItems[category].map((item) => {
                    const globalIndex = filteredItems.indexOf(item);
                    const isSelected = globalIndex === selectedIndex;
                    
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleSelectItem(item)}
                        className={`w-full text-left px-3 py-2 rounded-md flex items-center space-x-3 transition-colors ${
                          isSelected
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex-shrink-0 flex items-center justify-center">
                          {item.icon ? (
                            item.icon
                          ) : item.type === 'action' ? (
                            <PlusIcon className="w-5 h-5" />
                          ) : item.type === 'recent' ? (
                            <ClockIcon className="w-5 h-5" />
                          ) : (
                            <ArrowRightIcon className="w-5 h-5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{item.label}</div>
                          {item.description && (
                            <div className="text-xs text-gray-500 truncate">
                              {item.description}
                            </div>
                          )}
                        </div>
                        {item.shortcut && (
                          <div className="flex-shrink-0 text-xs text-gray-400">
                            <kbd className="px-2 py-1 bg-gray-100 rounded">
                              {item.shortcut}
                            </kbd>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <kbd className="px-1.5 py-0.5 bg-white border rounded">↑↓</kbd>
                <span>Navigate</span>
              </div>
              <div className="flex items-center space-x-1">
                <kbd className="px-1.5 py-0.5 bg-white border rounded">↵</kbd>
                <span>Select</span>
              </div>
              <div className="flex items-center space-x-1">
                <kbd className="px-1.5 py-0.5 bg-white border rounded">Esc</kbd>
                <span>Close</span>
              </div>
            </div>
            <div className="text-gray-400">
              {filteredItems.length} {filteredItems.length === 1 ? 'result' : 'results'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
